import React, { useEffect, useMemo, useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { t } from "i18next";
import { FormProvider, useForm } from "react-hook-form";

import { PLATFORM_TYPES } from "@/src/components/offers/offerPlan.constants";
import {
  apiToPlanForm,
  createPlanSchema,
  mergePlanWithFormValues,
} from "@/src/components/offers/offerPlanAdapter";
import { getOfferPlanTypeIcon } from "@/src/components/offers/offersFormParams/offersFormParams";
import { Button } from "@/src/components/ui/button";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/src/components/ui/drawer";
import { ScrollArea } from "@/src/components/ui/scroll-area";
import { useOfferPlanMutations } from "@/src/hooks/useOfferPlanMutations";

import OfferPlanDrawerSections from "./OfferPlanDrawerSections";
import PlanDrawerContainerContext from "./plan-drawer-context";
import usePlanFormDerivations from "./usePlanFormDerivations";

import type { JSX } from "react";

import type { ProviderPlan } from "@/src/api/OfferAPI.types";
import type {
  FlatOfferPlan,
  PlanFormValues,
} from "@/src/components/offers/offerPlanAdapter";
import type {
  AvailablePlatform,
  Retailer,
} from "@/src/components/offers/offersFormParams/offersFormParams";
import type { OfferPlanTypeValue } from "@/src/models/OfferPlan.types";
import type { OfferPlanType } from "@/src/types";

interface OfferPlanDrawerProps {
  open: boolean;
  plan: OfferPlanType;
  planType: OfferPlanTypeValue;
  offerId: string;
  projectSlug: string;
  availablePlatform: AvailablePlatform;
  availableProviderPlanList: ProviderPlan[];
  availableRetailerList: Retailer[];
  onClose: () => void;
  onSaved: (plan: FlatOfferPlan) => void;
}

function OfferPlanDrawer({
  open,
  plan,
  planType,
  offerId,
  projectSlug,
  availablePlatform,
  availableProviderPlanList,
  availableRetailerList,
  onClose,
  onSaved,
}: OfferPlanDrawerProps): JSX.Element {
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(
    null,
  );
  const { savePlan, isSaving } = useOfferPlanMutations({
    projectSlug,
    offerId,
  });

  const isEdu = plan.platform === PLATFORM_TYPES.EDU;

  const initialFormValues = useMemo(() => apiToPlanForm(plan), [plan]);

  const resolver = useMemo(
    () => zodResolver(createPlanSchema(availableProviderPlanList)),
    [availableProviderPlanList],
  );

  const methods = useForm<PlanFormValues>({
    defaultValues: initialFormValues,
    resolver,
  });

  usePlanFormDerivations(methods, availableProviderPlanList);

  useEffect(() => {
    // Re-sync on every open, not just when `initialFormValues` changes: the
    // same plan reference is reused across a cancel + reopen (nothing was
    // saved), so relying on `initialFormValues` alone would leave the
    // previous, discarded edits in the form.
    if (open) {
      methods.reset(initialFormValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialFormValues]);

  // Providers already persisted on the plan are locked (can't be removed) —
  // taken from the original plan, NOT the mutating draft. New plans lock none.
  const lockedProviderPlanList = plan.isNew ? [] : (plan.provider_plans ?? []);

  const onValid = async (values: PlanFormValues): Promise<void> => {
    const updatedPlan = mergePlanWithFormValues(plan, values);

    try {
      const saved = await savePlan(updatedPlan);
      // Reconcile the client temp uuid (new plans) with the server-assigned one
      // so later edits/deletes target the persisted plan, not the draft.
      onSaved({ ...updatedPlan, uuid: saved.uuid });
      onClose();
    } catch {
      // Save failed — the mutation's onError already surfaced a toast.
    }
  };

  return (
    <Drawer
      direction="right"
      onOpenChange={(updatedPlan: boolean) => !updatedPlan && onClose()}
      open={open}
    >
      <DrawerContent className="bg-card right-0 ml-auto flex h-full w-full flex-col sm:max-w-[480px]!">
        <div ref={setPortalContainer} className="flex min-h-0 flex-1 flex-col">
          <PlanDrawerContainerContext.Provider value={portalContainer}>
            {/* eslint-disable-next-line react/jsx-props-no-spreading */}
            <FormProvider {...methods}>
              <DrawerHeader className="border-border border-b">
                <DrawerTitle className="text-lg">
                  {isEdu ? (
                    t("editor-offers:configurationTitle")
                  ) : (
                    <div className="flex items-center gap-2.5">
                      <img
                        alt="drawer-offer-title-icon"
                        className="size-5"
                        src={getOfferPlanTypeIcon(planType)}
                      />
                      {t(`editor-offers:drawerTitle_${planType}`)}
                    </div>
                  )}
                </DrawerTitle>
                <DrawerDescription>
                  {t(
                    isEdu
                      ? "editor-offers:drawerSubtitle_edu"
                      : "editor-offers:drawerSubtitle",
                  )}
                </DrawerDescription>
              </DrawerHeader>

              <ScrollArea className="min-h-0 flex-1">
                <div className="flex flex-col gap-8 px-6 py-8">
                  <OfferPlanDrawerSections
                    availablePlatform={availablePlatform}
                    availableProviderPlanList={availableProviderPlanList}
                    lockedProviderPlanList={lockedProviderPlanList}
                    plan={plan}
                    retailerList={availableRetailerList}
                  />
                </div>
              </ScrollArea>

              <DrawerFooter className="border-border flex-row justify-end gap-2 border-t">
                <Button
                  disabled={isSaving}
                  onClick={onClose}
                  type="button"
                  variant="outline"
                >
                  {t("editor-offers:cancel")}
                </Button>
                <Button
                  disabled={isSaving}
                  onClick={methods.handleSubmit(onValid)}
                  type="button"
                >
                  {t("editor-offers:save")}
                </Button>
              </DrawerFooter>
            </FormProvider>
          </PlanDrawerContainerContext.Provider>
        </div>
      </DrawerContent>
    </Drawer>
  );
}

export default OfferPlanDrawer;
